(() => {
  class MobileHeaderSearchController {
    constructor(section) {
      this.section = section;
      this.header = document.querySelector('.section-header');
      this.panel = section.querySelector('.ph-mobile-search__panel');
      this.panelHome = this.panel?.parentElement;
      this.portal = null;
      this.overlaySurface = null;
      this.pageBackdrop = null;
      this.desktopClipSvg = null;
      this.desktopClipPath = null;
      this.desktopWheelState = null;
      this.pillWheelState = null;
      this.pillDrag = null;
      this.pillDragFrame = null;
      this.pillSuppressClick = false;
      this.popular = this.panel?.querySelector('.ph-mobile-search__popular');
      this.pills = this.popular?.querySelector('.ph-discovery__pills');
      this.productList = this.panel?.querySelector('[data-ph-discovery-products]');
      this.searchForm = section.querySelector('form[role="search"]');
      this.input = this.searchForm?.querySelector('input[type="search"]');
      this.desktopInput = this.header?.querySelector('.header__search-bar input[type="search"]');
      this.desktopSearch = this.desktopInput?.closest('.header__search-bar');
      this.desktopDock = this.panel?.querySelector('[data-ph-search-desktop-dock]');
      this.desktopHeaderRow = this.header?.querySelector('header.header');
      this.poolCache = new Map();
      this.launchButton = this.panel?.querySelector('[data-ph-mobile-search-launch]');
      this.trigger = this.header?.querySelector('[data-ph-mobile-search-trigger]');
      this.closeButton = section.querySelector('[data-ph-mobile-search-close]');
      this.predictive = section.querySelector('predictive-search');
      this.overlayBrand = section.querySelector('.ph-mobile-search__brand');
      this.breakpoint = window.matchMedia('(max-width: 989.98px)');
      this.active = false;
      this.desktopActive = false;
      this.closing = false;
      this.overlayAnimation = null;
      this.scrollBoundary = 0;
      this.lastScrollY = -1;
      this.lastViewportWidth = document.documentElement.clientWidth;
      this.lastBreakpointState = this.breakpoint.matches;
      this.returnFocusTo = null;
      this.savedPageScrollY = null;
      this.preventingScroll = false;
      this.touchStartY = null;
      this.touchStartX = null;
      this.viewportFrame = null;
      this.desktopPositionFrame = null;
      this.tabletBodyLock = null;
      this.tabletTouchGesture = null;
      this.tabletMomentumFrame = null;
      this.desktopResizeObserver = typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => this.queueDesktopPositionSync())
        : null;
      this.overlayLayoutHeight = 0;
      this.productSequence = 0;
      this.productAbort = null;
      this.abort = new AbortController();

      if (!this.header || !this.panel || !this.popular || !this.input || !this.trigger || !this.closeButton) return;

      const signal = this.abort.signal;
      this.trigger.addEventListener('click', () => this.open(true), { signal });
      if (this.launchButton && this.searchForm) {
        this.launchButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(false, true);
        }, { signal });
        this.searchForm.inert = true;
        this.section.classList.add('ph-search-launch-ready');
      }
      this.closeButton.addEventListener('click', (event) => {
        if (event.detail > 0) this.returnFocusTo = null;
        this.close();
      }, { signal });
      this.input.addEventListener('focus', () => this.queueViewportSync(), { signal });
      this.input.addEventListener('blur', () => this.queueViewportSync(), { signal });
      this.input.addEventListener('input', () => this.updateQueryState(), { signal });
      if (this.pills) {
        this.pills.addEventListener('pointerdown', (event) => this.onPillsPointerDown(event), { signal });
        this.pills.addEventListener('pointermove', (event) => this.onPillsPointerMove(event), { signal });
        this.pills.addEventListener('pointerup', (event) => this.finishPillsPointerDrag(event), { signal });
        this.pills.addEventListener('pointercancel', (event) => this.finishPillsPointerDrag(event), { signal });
        this.pills.addEventListener('lostpointercapture', (event) => this.finishPillsPointerDrag(event), { signal });
        this.pills.addEventListener('click', (event) => {
          if (!this.pillSuppressClick) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.pillSuppressClick = false;
        }, { capture: true, signal });
      }

      this.desktopInput?.addEventListener('focus', () => {
        if (!this.breakpoint.matches) this.openDesktop();
      }, { signal });
      this.desktopInput?.addEventListener('input', () => {
        if (this.breakpoint.matches) return;
        if (!this.desktopActive && document.activeElement === this.desktopInput) this.openDesktop();
        this.updateDesktopQueryState();
      }, { signal });

      document.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      document.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
      document.addEventListener('touchmove', (event) => this.preventBackgroundTouch(event), { passive: false, capture: true, signal });
      document.addEventListener('touchend', () => this.finishTouchTabletGesture(), { passive: true, signal });
      document.addEventListener('touchcancel', () => this.finishTouchTabletGesture(true), { passive: true, signal });
      document.addEventListener('wheel', (event) => this.preventBackgroundWheel(event), { passive: false, capture: true, signal });
      document.addEventListener('scroll', (event) => this.onScrollableScroll(event), { capture: true, passive: true, signal });
      window.addEventListener('scroll', () => {
        if (this.active) this.queueViewportSync();
        if (this.desktopActive) this.queueDesktopPositionSync();
        this.updateCompact();
      }, { passive: true, signal });
      window.addEventListener('resize', () => {
        this.onResize();
        this.queueViewportSync();
        this.syncDesktopPosition();
      }, { passive: true, signal });
      window.visualViewport?.addEventListener('resize', () => this.queueViewportSync(), { passive: true, signal });
      window.visualViewport?.addEventListener('scroll', () => this.queueViewportSync(), { passive: true, signal });
      window.addEventListener('pageshow', () => this.refresh(), { signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      section.addEventListener('shopify:section:unload', () => this.destroy(), { signal });
      this.refresh();
    }

    refresh() {
      if (!this.header) return;
      if (!this.breakpoint.matches) {
        if (this.active) this.close(false);
        this.header.classList.remove('ph-mobile-search-compact');
        if (this.desktopActive) this.syncDesktopPosition();
        return;
      }
      if (this.desktopActive) this.finishDesktopClose();
      const documentY = window.scrollY || window.pageYOffset || 0;
      const sectionBottom = this.section.getBoundingClientRect().bottom + documentY;
      const headerHeight = this.header.offsetHeight;
      this.scrollBoundary = Math.max(0, sectionBottom - headerHeight);
      this.updateCompact(true);
      if (this.active) {
        this.syncOverlayHeader();
        this.syncViewport();
      }
    }

    syncOverlayHeader() {
      const overlay = this.portal || this.section;
      const headerRow = this.header?.querySelector('header.header');
      const menuButton = this.header?.querySelector('.header__icon--menu');
      const visibleLogo = [...(this.header?.querySelectorAll('.header__heading-logo') || [])].find((logo) => {
        const rect = logo.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && getComputedStyle(logo).display !== 'none';
      });
      if (!headerRow) return;
      const row = headerRow.getBoundingClientRect();
      if (!row.height) return;
      const px = (value) => `${Math.round(value * 100) / 100}px`;
      overlay.style.setProperty('--ph-overlay-header-height', px(row.height));
      if (visibleLogo && this.overlayBrand) {
        const rect = visibleLogo.getBoundingClientRect();
        overlay.style.setProperty('--ph-overlay-logo-left', px(rect.left));
        overlay.style.setProperty('--ph-overlay-logo-top', px(rect.top - row.top));
        overlay.style.setProperty('--ph-overlay-logo-width', px(rect.width));
        overlay.style.setProperty('--ph-overlay-logo-height', px(rect.height));
        overlay.style.setProperty('--ph-overlay-logo-transform', 'none');
      }
      if (menuButton) {
        const rect = menuButton.getBoundingClientRect();
        if (rect.width && rect.height) {
          overlay.style.setProperty('--ph-overlay-close-left', px(rect.left));
          overlay.style.setProperty('--ph-overlay-close-top', px(rect.top - row.top));
          overlay.style.setProperty('--ph-overlay-close-width', px(rect.width));
          overlay.style.setProperty('--ph-overlay-close-height', px(rect.height));
          overlay.style.setProperty('--ph-overlay-close-transform', 'none');
        }
      }
    }

    queueViewportSync() {
      if (!this.active || !this.portal || this.viewportFrame !== null) return;
      this.viewportFrame = requestAnimationFrame(() => {
        this.viewportFrame = null;
        this.syncViewport();
      });
    }

    syncViewport() {
      if (!this.active || !this.portal) return;
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop ?? 0;
      const left = viewport?.offsetLeft ?? 0;
      const width = viewport?.width ?? window.innerWidth;
      const visibleHeight = viewport?.height ?? window.innerHeight;
      const layoutHeight = this.overlayLayoutHeight || window.innerHeight;
      if (![top, left, width, visibleHeight, layoutHeight].every(Number.isFinite) ||
          width <= 0 || visibleHeight <= 0 || layoutHeight <= 0) return;
      const px = (value) => `${Math.round(value * 100) / 100}px`;
      this.portal.style.setProperty('--ph-overlay-viewport-top', px(top));
      this.portal.style.setProperty('--ph-overlay-viewport-left', px(left));
      this.portal.style.setProperty('--ph-overlay-viewport-width', px(width));
      this.portal.style.setProperty('--ph-overlay-viewport-height', px(layoutHeight));
      this.portal.style.setProperty('--ph-overlay-viewport-bottom', px(top + layoutHeight));
      this.portal.style.setProperty('--ph-overlay-keyboard-cover', px(Math.max(0, layoutHeight - visibleHeight)));
      this.updateResultsTop();
    }

    queueDesktopPositionSync() {
      if (!this.desktopActive || this.desktopPositionFrame !== null) return;
      this.desktopPositionFrame = requestAnimationFrame(() => {
        this.desktopPositionFrame = null;
        this.syncDesktopPosition();
      });
    }

    syncDesktopPosition() {
      if (!this.desktopActive || !this.portal || !this.desktopSearch || !this.desktopInput) return;
      const rect = this.desktopSearch.getBoundingClientRect();
      if (rect.width < 20 || rect.height < 20) return;

      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = window.innerHeight;
      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      const px = (value) => `${Math.round(value * 100) / 100}px`;
      const inset = 0;
      const panelWidth = Math.min(rect.width + inset * 2, viewportWidth - 32);
      const panelLeft = clamp(rect.left - inset, 16, Math.max(16, viewportWidth - panelWidth - 16));
      const panelTop = clamp(rect.top - 12, 8, Math.max(8, viewportHeight - 180));
      const bottomClearance = clamp(viewportHeight * 0.05, 28, 56);
      const panelBottom = Math.max(panelTop + 110, viewportHeight - bottomClearance);
      const resultsTop = rect.bottom + 12;
      const resultsHeight = Math.max(0, panelBottom - resultsTop);

      this.portal.style.setProperty('--ph-discovery-desktop-top', px(panelTop));
      this.portal.style.setProperty('--ph-discovery-desktop-left', px(panelLeft));
      this.portal.style.setProperty('--ph-discovery-desktop-width', px(panelWidth));
      this.portal.style.setProperty('--ph-discovery-desktop-bottom', px(bottomClearance));
      this.portal.style.setProperty('--ph-discovery-desktop-header-height', px(resultsTop - panelTop));
      if (this.desktopClipPath) {
        const left = panelLeft;
        const top = panelTop;
        const right = left + panelWidth;
        const bottom = panelBottom;
        const radius = Math.min(
          parseFloat(getComputedStyle(this.panel).borderTopLeftRadius) || 10,
          (right - left) / 2,
          (bottom - top) / 2
        );
        const outerWidth = Math.max(viewportWidth, window.innerWidth);
        const outerHeight = Math.max(viewportHeight, document.documentElement.clientHeight);
        this.desktopClipPath.setAttribute('d', [
          'M 0 0 H', outerWidth, 'V', outerHeight, 'H 0 Z',
          'M', left + radius, top,
          'H', right - radius,
          'A', radius, radius, '0 0 1', right, top + radius,
          'V', bottom - radius,
          'A', radius, radius, '0 0 1', right - radius, bottom,
          'H', left + radius,
          'A', radius, radius, '0 0 1', left, bottom - radius,
          'V', top + radius,
          'A', radius, radius, '0 0 1', left + radius, top,
          'Z'
        ].join(' '));
      }
      const headerBottom = this.header?.getBoundingClientRect()?.bottom || resultsTop;
      this.portal.style.setProperty('--ph-discovery-desktop-header-dim-height', px(Math.min(viewportHeight, Math.max(0, headerBottom))));

      // Results stay inside the original predictive-search custom element.
      // Anchor their dropdown to the original form's bounding rectangle:
      // no styling or DOM relocation of the form/input is necessary.
      const predictiveAnchor = this.desktopInput.closest?.('form[role="search"]') || this.desktopSearch;
      const predictiveRect = predictiveAnchor.getBoundingClientRect();
      this.desktopSearch.style.setProperty('--ph-discovery-predictive-left', px(panelLeft - predictiveRect.left));
      this.desktopSearch.style.setProperty('--ph-discovery-predictive-top', px(resultsTop - predictiveRect.top));
      this.desktopSearch.style.setProperty('--ph-discovery-predictive-width', px(panelWidth));
      this.desktopSearch.style.setProperty('--ph-discovery-predictive-height', px(resultsHeight));
    }

    clearDesktopPosition() {
      if (!this.desktopSearch) return;
      ['--ph-discovery-predictive-left', '--ph-discovery-predictive-top',
        '--ph-discovery-predictive-width', '--ph-discovery-predictive-height']
        .forEach((key) => this.desktopSearch.style.removeProperty(key));
    }

    updateDesktopQueryState() {
      this.portal?.classList.toggle('ph-search-desktop-has-query', Boolean(this.desktopInput?.value.trim()));
    }

    fadePanel(fadeIn, done) {
      const targets = this.desktopActive
        ? [
            this.pageBackdrop,
            this.portal,
            this.desktopSearch?.querySelector('predictive-search[open] [data-predictive-search]')
          ].filter(Boolean)
        : [this.overlaySurface, this.panel.querySelector('.ph-mobile-search__input'), this.popular].filter(Boolean);
      const currentOpacities = targets.map((element) => {
        const value = Number.parseFloat(getComputedStyle(element).opacity);
        return Number.isFinite(value) ? value : 1;
      });
      const resumingDesktopFade = this.desktopActive && fadeIn && Boolean(this.overlayAnimation);
      this.overlayAnimation?.cancel();
      this.overlayAnimation = null;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reduceMotion ? 0 : (this.desktopActive ? (fadeIn ? 135 : 220) : (fadeIn ? 170 : 150));
      if (!duration || targets.some((element) => typeof element.animate !== 'function')) {
        done?.();
        return;
      }
      const animations = targets.map((element, index) => element.animate(
        [{ opacity: fadeIn ? (resumingDesktopFade ? currentOpacities[index] : 0) : currentOpacities[index] }, { opacity: fadeIn ? 1 : 0 }],
        { duration, easing: this.desktopActive && !fadeIn ? 'ease-in-out' : 'ease-out', fill: 'forwards' }
      ));
      const group = { cancel: () => animations.forEach((animation) => animation.cancel()) };
      this.overlayAnimation = group;
      animations[0].finished.then(() => {
        if (this.overlayAnimation !== group) return;
        this.overlayAnimation = null;
        done?.();
        group.cancel();
      }).catch(() => {});
    }

    updateCompact(force = false) {
      if (!this.breakpoint.matches || !this.header || this.active) return;
      const y = window.scrollY || window.pageYOffset || 0;
      if (!force && y === this.lastScrollY) return;
      this.lastScrollY = y;
      const compact = y >= this.scrollBoundary;
      if (this.header.classList.contains('ph-mobile-search-compact') !== compact) {
        this.header.classList.toggle('ph-mobile-search-compact', compact);
      }
    }

    onResize() {
      const nextWidth = document.documentElement.clientWidth;
      const nextBreakpointState = this.breakpoint.matches;
      if (Math.abs(nextWidth - this.lastViewportWidth) < 3 && nextBreakpointState === this.lastBreakpointState) return;
      this.lastViewportWidth = nextWidth;
      this.lastBreakpointState = nextBreakpointState;
      if (!nextBreakpointState && this.active) this.close(false);
      if (nextBreakpointState && this.desktopActive) this.finishDesktopClose();
      this.refresh();
    }

    mountOverlay(mode) {
      if (this.portal || !this.panelHome) return;
      if (mode === 'mobile') {
        this.overlayLayoutHeight = Math.max(1, Math.round(window.innerHeight || document.documentElement.clientHeight));
      }
      const portal = document.createElement('div');
      portal.className = 'ph-mobile-search-section ph-mobile-search-active ph-mobile-search-overlay-portal ph-search-' + mode + '-mode';
      portal.setAttribute('data-ph-search-overlay', '');
      const surface = document.createElement('div');
      surface.className = 'ph-mobile-search-overlay-surface';
      surface.setAttribute('aria-hidden', 'true');
      portal.appendChild(surface);
      // On desktop, share the real header's stacking context. This allows
      // the untouched search field to sit above the dark/blurred backdrop.
      // On smaller screens keep the original body-level full-screen portal.
      const host = mode === 'desktop' ? (this.desktopHeaderRow || this.header) : document.body;
      host.appendChild(portal);
      portal.appendChild(this.panel);
      this.portal = portal;
      this.overlaySurface = surface;
      if (mode === 'desktop') {
        const pageBackdrop = document.createElement('div');
        pageBackdrop.className = 'ph-search-desktop-page-backdrop';
        pageBackdrop.setAttribute('aria-hidden', 'true');
        document.body.appendChild(pageBackdrop);
        this.pageBackdrop = pageBackdrop;

        const namespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(namespace, 'svg');
        const defs = document.createElementNS(namespace, 'defs');
        const clip = document.createElementNS(namespace, 'clipPath');
        const path = document.createElementNS(namespace, 'path');
        const clipId = 'ph-search-round-cutout-' + (this.section.id || 'main').replace(/[^a-zA-Z0-9_-]/g, '');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.setAttribute('width', '0');
        svg.setAttribute('height', '0');
        svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';
        clip.setAttribute('id', clipId);
        clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
        path.setAttribute('clip-rule', 'evenodd');
        path.setAttribute('fill-rule', 'evenodd');
        clip.appendChild(path);
        defs.appendChild(clip);
        svg.appendChild(defs);
        document.body.appendChild(svg);
        this.desktopClipSvg = svg;
        this.desktopClipPath = path;
        pageBackdrop.style.setProperty('clip-path', 'url("#' + clipId + '")', 'important');
        pageBackdrop.style.setProperty('-webkit-clip-path', 'url("#' + clipId + '")', 'important');

        pageBackdrop.addEventListener('click', () => {
          if (this.desktopActive) this.closeDesktop();
        }, { signal: this.abort.signal });
      }
      surface.addEventListener('click', () => {
        if (this.desktopActive) {
          this.blurSearchInputs();
          this.closeDesktop();
        }
      }, { signal: this.abort.signal });
    }

    unmountOverlay() {
      this.pageBackdrop?.remove();
      this.pageBackdrop = null;
      this.desktopClipSvg?.remove();
      this.desktopClipSvg = null;
      this.desktopClipPath = null;
      if (!this.portal) return;
      if (this.panelHome?.isConnected) this.panelHome.appendChild(this.panel);
      if (this.viewportFrame !== null) {
        cancelAnimationFrame(this.viewportFrame);
        this.viewportFrame = null;
      }
      this.portal.remove();
      this.portal = null;
      this.overlaySurface = null;
      this.overlayLayoutHeight = 0;
    }

    open(fromTrigger, focusInput = false) {
      if (!this.breakpoint.matches || this.active || this.desktopActive) return;
      this.blurSearchInputs();
      this.savedPageScrollY = window.scrollY || window.pageYOffset || 0;
      const sectionHeight = this.section.getBoundingClientRect().height;
      this.section.style.setProperty('--ph-mobile-search-slot-height', `${sectionHeight}px`);
      this.section.classList.add('ph-mobile-search-slot-occupied');
      this.active = true;
      this.closing = false;
      this.returnFocusTo = fromTrigger ? this.trigger : (this.launchButton || this.header.querySelector('.header__heading-link'));
      this.mountOverlay('mobile');
      if (this.searchForm) this.searchForm.inert = false;
      if (this.launchButton) this.launchButton.setAttribute('aria-expanded', 'true');
      this.syncOverlayHeader();
      this.syncViewport();
      this.populateDiscovery();
      this.fadePanel(true);
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('aria-label', 'Search Procters Health');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.updateQueryState();
      if (focusInput) this.input.focus({ preventScroll: true });
      this.queueViewportSync();
    }

    openDesktop() {
      if (this.breakpoint.matches || this.active || !this.desktopInput || !this.desktopDock) return;
      if (this.desktopActive) {
        if (this.closing) {
          this.closing = false;
          this.fadePanel(true);
        }
        return;
      }
      this.desktopActive = true;
      this.closing = false;
      // Keep the existing input in place. The portal simply draws a card
      // behind it, and puts the dimmed/blurred layer below the search field.
      document.documentElement.classList.add('ph-search-discovery-desktop-open');
      this.mountOverlay('desktop');
      this.lockTouchTabletPage();
      this.syncDesktopPosition();
      this.desktopResizeObserver?.observe(this.desktopSearch);
      this.desktopResizeObserver?.observe(this.header);
      const announcement = document.querySelector('.section-announcement-bar');
      if (announcement) this.desktopResizeObserver?.observe(announcement);
      this.updateDesktopQueryState();
      this.populateDiscovery();
      this.fadePanel(true);
    }

    closeDesktop() {
      if (!this.desktopActive || this.closing) return;
      this.closing = true;
      this.fadePanel(false, () => this.finishDesktopClose());
    }

    finishDesktopClose() {
      if (!this.desktopActive) return;
      this.overlayAnimation?.cancel();
      this.overlayAnimation = null;
      this.desktopActive = false;
      this.closing = false;
      this.finishTouchTabletGesture(true);
      this.desktopResizeObserver?.disconnect();
      if (this.desktopWheelState?.frame !== null && this.desktopWheelState?.frame !== undefined) {
        cancelAnimationFrame(this.desktopWheelState.frame);
      }
      this.desktopWheelState = null;
      this.stopPillWheelAnimation();
      if (this.pillDragFrame !== null) cancelAnimationFrame(this.pillDragFrame);
      this.pillDragFrame = null;
      this.pillDrag = null;
      this.pills?.classList.remove('ph-discovery__pills--dragging');
      if (this.desktopPositionFrame !== null) {
        cancelAnimationFrame(this.desktopPositionFrame);
        this.desktopPositionFrame = null;
      }
      // Only deliberate dismissal removes focus; typing never reinitialises
      // the search form or changes its position in the DOM.
      if (document.activeElement === this.desktopInput) this.desktopInput.blur();
      this.desktopInput?.closest('predictive-search')?.close?.();
      this.clearDesktopPosition();
      this.unmountOverlay();
      document.documentElement.classList.remove('ph-search-discovery-desktop-open');
      this.unlockTouchTabletPage();
    }

    restorePagePosition(y) {
      if (this.preventingScroll || Math.abs((window.scrollY || 0) - y) < 1) return;
      this.preventingScroll = true;
      const html = document.documentElement;
      const previous = html.style.getPropertyValue('scroll-behavior');
      const priority = html.style.getPropertyPriority('scroll-behavior');
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(0, y);
      if (previous) html.style.setProperty('scroll-behavior', previous, priority);
      else html.style.removeProperty('scroll-behavior');
      this.preventingScroll = false;
    }

    blurSearchInputs() {
      const focused = document.activeElement;
      if (focused === this.input || focused === this.desktopInput) focused.blur();
    }

    onPointerDown(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const tappedInput = target === this.input || target === this.desktopInput || target.closest('input[type="search"]');
      if (this.desktopActive && !this.portal?.contains(target) && !this.desktopSearch?.contains(target) &&
          !(this.isTouchTabletDesktop() && this.isPointInside(this.panel, event.clientX, event.clientY))) {
        if (event.cancelable) event.preventDefault();
        this.closeDesktop();
        return;
      }
      if (!tappedInput && !(this.desktopActive && this.desktopSearch?.contains(target))) this.blurSearchInputs();
    }


    isTouchTabletDesktop() {
      const width = document.documentElement.clientWidth || window.innerWidth;
      const touchPoints = navigator.maxTouchPoints || 0;
      if (width < 990 || width > 1440 || touchPoints < 2) return false;
      const ua = navigator.userAgent || '';
      return window.matchMedia('(any-pointer: coarse)').matches ||
        /iPad|Android/i.test(ua) || navigator.platform === 'MacIntel';
    }

    isPointInside(element, x, y) {
      if (!element || !element.isConnected || !Number.isFinite(x) || !Number.isFinite(y)) return false;
      const bounds = element.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0 &&
        x >= bounds.left && x <= bounds.right &&
        y >= bounds.top && y <= bounds.bottom;
    }

    lockTouchTabletPage() {
      if (!this.isTouchTabletDesktop() || this.tabletBodyLock) return;
      const body = document.body;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow'];
      const originals = properties.map((property) => ({
        property,
        value: body.style.getPropertyValue(property),
        priority: body.style.getPropertyPriority(property)
      }));
      const scrollY = window.scrollY || window.pageYOffset || 0;
      this.tabletBodyLock = { scrollY, originals };
      body.style.setProperty('position', 'fixed', 'important');
      body.style.setProperty('top', String(-scrollY) + 'px', 'important');
      body.style.setProperty('left', '0', 'important');
      body.style.setProperty('right', '0', 'important');
      body.style.setProperty('width', '100%', 'important');
      body.style.setProperty('overflow', 'hidden', 'important');
      document.documentElement.classList.add('ph-search-ipad-scroll-fix');
    }

    unlockTouchTabletPage() {
      const lock = this.tabletBodyLock;
      if (!lock) return;
      this.cancelTouchTabletMomentum();
      this.tabletTouchGesture = null;
      document.documentElement.classList.remove('ph-search-ipad-scroll-fix');
      for (const { property, value, priority } of lock.originals) {
        if (value) document.body.style.setProperty(property, value, priority);
        else document.body.style.removeProperty(property);
      }
      this.tabletBodyLock = null;
      this.restorePagePosition(lock.scrollY);
    }

    cancelTouchTabletMomentum() {
      if (this.tabletMomentumFrame !== null) cancelAnimationFrame(this.tabletMomentumFrame);
      this.tabletMomentumFrame = null;
    }

    beginTouchTabletGesture(event, touch) {
      if (!this.desktopActive || !this.isTouchTabletDesktop() || !touch) return;
      this.cancelTouchTabletMomentum();
      if (event.target?.closest?.('input, textarea, select')) {
        this.tabletTouchGesture = null;
        return;
      }
      const predictive = this.desktopSearch?.querySelector('predictive-search[open] [data-predictive-search]');
      const hasPredictive = predictive?.isConnected && getComputedStyle(predictive).display !== 'none';
      const vertical = hasPredictive ? predictive : this.popular;
      const inside = this.panel?.contains(event.target) ||
        this.desktopSearch?.contains(event.target) ||
        this.isPointInside(this.panel, touch.clientX, touch.clientY) ||
        (hasPredictive && this.isPointInside(predictive, touch.clientX, touch.clientY));
      const inPills = !hasPredictive && this.pills && (
        this.pills.contains(event.target) || this.isPointInside(this.pills, touch.clientX, touch.clientY));
      this.tabletTouchGesture = inside && vertical?.isConnected ? {
        vertical,
        pills: inPills ? this.pills : null,
        lastX: touch.clientX,
        lastY: touch.clientY,
        lastTime: performance.now(),
        totalX: 0,
        totalY: 0,
        axis: null,
        started: false,
        velocity: 0
      } : null;
    }

    moveTouchTabletGesture(event, touch) {
      const gesture = this.tabletTouchGesture;
      if (gesture && touch) {
        const now = performance.now();
        const dx = touch.clientX - gesture.lastX;
        const dy = touch.clientY - gesture.lastY;
        const dt = Math.max(8, now - gesture.lastTime);
        gesture.totalX += dx;
        gesture.totalY += dy;
        gesture.lastX = touch.clientX;
        gesture.lastY = touch.clientY;
        gesture.lastTime = now;
        if (!gesture.started && Math.hypot(gesture.totalX, gesture.totalY) > 5) {
          gesture.started = true;
          gesture.axis = gesture.pills && Math.abs(gesture.totalX) > Math.abs(gesture.totalY) ? 'x' : 'y';
        }
        if (gesture.started) {
          const scroller = gesture.axis === 'x' ? gesture.pills : gesture.vertical;
          const property = gesture.axis === 'x' ? 'scrollLeft' : 'scrollTop';
          const max = Math.max(0, gesture.axis === 'x'
            ? scroller.scrollWidth - scroller.clientWidth
            : scroller.scrollHeight - scroller.clientHeight);
          const amount = -(gesture.axis === 'x' ? dx : dy);
          scroller[property] = Math.max(0, Math.min(max, scroller[property] + amount));
          gesture.velocity = gesture.velocity * 0.55 + Math.max(-3, Math.min(3, amount / dt)) * 0.45;
        }
      }
      if (event.cancelable && (!gesture || gesture.started)) event.preventDefault();
    }

    finishTouchTabletGesture(cancel = false) {
      const gesture = this.tabletTouchGesture;
      this.tabletTouchGesture = null;
      if (cancel || !gesture?.started || !this.desktopActive || !this.tabletBodyLock) return;
      const scroller = gesture.axis === 'x' ? gesture.pills : gesture.vertical;
      const property = gesture.axis === 'x' ? 'scrollLeft' : 'scrollTop';
      let velocity = gesture.velocity;
      if (!scroller?.isConnected || Math.abs(velocity) < 0.06) return;
      this.cancelTouchTabletMomentum();
      const animate = () => {
        if (!this.desktopActive || !this.tabletBodyLock || !scroller.isConnected || Math.abs(velocity) < 0.045) {
          this.tabletMomentumFrame = null;
          return;
        }
        const max = Math.max(0, gesture.axis === 'x'
          ? scroller.scrollWidth - scroller.clientWidth
          : scroller.scrollHeight - scroller.clientHeight);
        const before = scroller[property];
        scroller[property] = Math.max(0, Math.min(max, before + velocity * 16));
        if (Math.abs(scroller[property] - before) < 0.1) {
          this.tabletMomentumFrame = null;
          return;
        }
        velocity *= 0.91;
        this.tabletMomentumFrame = requestAnimationFrame(animate);
      };
      this.tabletMomentumFrame = requestAnimationFrame(animate);
    }

    onTouchStart(event) {
      const touch = event.touches?.length === 1 ? event.touches[0] : null;
      this.touchStartY = touch ? touch.clientY : null;
      this.touchStartX = touch ? touch.clientX : null;
      this.beginTouchTabletGesture(event, touch);
    }

    onScrollableScroll(event) {
      if (!this.active) return;
      if (event.target === document || event.target === document.documentElement) return;
      this.blurSearchInputs();
    }

    canScrollWithinOverlay(target, direction) {
      const scrollable = target?.closest?.('.ph-mobile-search__popular, [data-predictive-search]');
      if (!scrollable || !(this.portal?.contains(scrollable) ||
        (this.desktopActive && this.desktopSearch?.contains(scrollable)))) return false;
      const max = scrollable.scrollHeight - scrollable.clientHeight;
      if (max < 2) return false;
      if (direction > 0) return scrollable.scrollTop > 1;
      if (direction < 0) return scrollable.scrollTop < max - 1;
      return true;
    }

    preventBackgroundTouch(event) {
      const touch = event.touches?.length === 1 ? event.touches[0] : null;
      const dx = touch && this.touchStartX !== null ? touch.clientX - this.touchStartX : 0;
      const dy = touch && this.touchStartY !== null ? touch.clientY - this.touchStartY : 0;
      if (this.desktopActive) {
        if (this.tabletBodyLock) {
          if (event.target?.closest?.('input, select, textarea')) return;
          this.moveTouchTabletGesture(event, touch);
          return;
        }
        if (!event.cancelable) return;
        const target = event.target;
        if (target?.closest?.('input, select, textarea')) return;
        const pills = target?.closest?.('.ph-discovery__pills');
        if (pills && this.portal?.contains(pills) && Math.abs(dx) > Math.abs(dy)) {
          if (pills.scrollWidth > pills.clientWidth + 1) return;
          event.preventDefault();
          return;
        }
        const activeResults = this.desktopSearch?.querySelector('predictive-search[open] [data-predictive-search]');
        const resultsVisible = activeResults?.isConnected && getComputedStyle(activeResults).display !== 'none';
        const scrollable = resultsVisible ? activeResults : this.popular;
        const inside = scrollable && (scrollable.contains(target) || scrollable === target);
        if (inside && Math.abs(dy) >= Math.abs(dx) && this.canScrollWithinOverlay(target, dy)) return;
        if (inside && Math.abs(dx) > Math.abs(dy)) return;
        event.preventDefault();
        return;
      }
      if (this.active && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        this.blurSearchInputs();
      }
      if (!this.active || !event.cancelable) return;
      if (event.target?.closest?.('input, select, textarea')) return;
      const pills = event.target?.closest?.('.ph-discovery__pills');
      if (pills && Math.abs(dx) > Math.abs(dy) && pills.scrollWidth > pills.clientWidth + 1) return;
      if (this.canScrollWithinOverlay(event.target, dy)) return;
      event.preventDefault();
    }

    stopPillWheelAnimation() {
      if (this.pillWheelState?.frame !== null && this.pillWheelState?.frame !== undefined) {
        cancelAnimationFrame(this.pillWheelState.frame);
      }
      this.pillWheelState = null;
    }

    scrollPillsWheel(pills, delta) {
      const max = Math.max(0, pills.scrollWidth - pills.clientWidth);
      if (max < 1 || !Number.isFinite(delta) || Math.abs(delta) < 0.01) return false;
      let state = this.pillWheelState;
      if (state?.element !== pills) {
        this.stopPillWheelAnimation();
        state = { element: pills, target: pills.scrollLeft, frame: null };
        this.pillWheelState = state;
      }
      state.target = Math.max(0, Math.min(max, state.target + delta));
      if (Math.abs(state.target - pills.scrollLeft) < 0.5) return false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        pills.scrollLeft = state.target;
        this.stopPillWheelAnimation();
        return true;
      }
      if (state.frame !== null) return true;

      let last = 0;
      const step = (now) => {
        if (this.pillWheelState !== state || !pills.isConnected || !this.desktopActive) {
          state.frame = null;
          if (this.pillWheelState === state) this.pillWheelState = null;
          return;
        }
        const duration = last ? Math.max(1, Math.min(now - last, 60)) : 16;
        last = now;
        state.target = Math.max(0, Math.min(Math.max(0, pills.scrollWidth - pills.clientWidth), state.target));
        const remaining = state.target - pills.scrollLeft;
        if (Math.abs(remaining) < 0.65) {
          pills.scrollLeft = state.target;
          state.frame = null;
          if (this.pillWheelState === state) this.pillWheelState = null;
          return;
        }
        pills.scrollLeft += remaining * (1 - Math.exp(-duration / 65));
        state.frame = requestAnimationFrame(step);
      };
      state.frame = requestAnimationFrame(step);
      return true;
    }

    onPillsPointerDown(event) {
      if (!this.desktopActive || this.breakpoint.matches || !this.pills ||
          event.pointerType !== 'mouse' || event.button !== 0 ||
          this.pills.scrollWidth <= this.pills.clientWidth + 1) return;
      this.pillSuppressClick = false;
      if (event.target.closest?.('.ph-discovery__pill')) return;
      this.stopPillWheelAnimation();
      this.pillDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startingScroll: this.pills.scrollLeft,
        target: this.pills.scrollLeft,
        moved: false
      };
    }

    onPillsPointerMove(event) {
      const drag = this.pillDrag;
      if (!drag || drag.pointerId !== event.pointerId || !this.pills) return;
      const dx = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(dx) < 6) return;
      if (!drag.moved) {
        drag.moved = true;
        this.pills.classList.add('ph-discovery__pills--dragging');
        try { this.pills.setPointerCapture(event.pointerId); } catch (_) {}
      }
      const max = Math.max(0, this.pills.scrollWidth - this.pills.clientWidth);
      drag.target = Math.max(0, Math.min(max, drag.startingScroll - dx));
      if (this.pillDragFrame === null) {
        this.pillDragFrame = requestAnimationFrame(() => {
          this.pillDragFrame = null;
          if (this.pillDrag && this.pills) this.pills.scrollLeft = this.pillDrag.target;
        });
      }
      if (event.cancelable) event.preventDefault();
    }

    finishPillsPointerDrag(event) {
      const drag = this.pillDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      if (this.pillDragFrame !== null) cancelAnimationFrame(this.pillDragFrame);
      this.pillDragFrame = null;
      if (drag.moved && this.pills) {
        this.pills.scrollLeft = drag.target;
        this.pillSuppressClick = true;
        if (event.cancelable) event.preventDefault();
      }
      this.pillDrag = null;
      this.pills?.classList.remove('ph-discovery__pills--dragging');
      if (this.pills?.hasPointerCapture?.(event.pointerId)) {
        this.pills.releasePointerCapture(event.pointerId);
      }
    }

    scrollDesktopWheel(scrollable, delta) {
      const max = Math.max(0, scrollable.scrollHeight - scrollable.clientHeight);
      if (max < 1 || !Number.isFinite(delta) || Math.abs(delta) < 0.01) return false;

      let state = this.desktopWheelState;
      if (state && state.element !== scrollable) {
        if (state.frame !== null) cancelAnimationFrame(state.frame);
        this.desktopWheelState = null;
        state = null;
      }
      if (!state) {
        state = { element: scrollable, target: scrollable.scrollTop, frame: null };
        this.desktopWheelState = state;
      }

      state.target = Math.max(0, Math.min(max, state.target + delta));
      if (Math.abs(state.target - scrollable.scrollTop) < 0.5) return false;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        scrollable.scrollTop = state.target;
        this.desktopWheelState = null;
        return true;
      }
      if (state.frame !== null) return true;

      let last = 0;
      const step = (now) => {
        if (this.desktopWheelState !== state || !this.desktopActive ||
            !scrollable.isConnected || getComputedStyle(scrollable).display === 'none') {
          state.frame = null;
          if (this.desktopWheelState === state) this.desktopWheelState = null;
          return;
        }
        state.target = Math.max(0, Math.min(
          Math.max(0, scrollable.scrollHeight - scrollable.clientHeight),
          state.target
        ));
        const elapsed = last ? Math.max(1, Math.min(now - last, 60)) : 16;
        last = now;
        const difference = state.target - scrollable.scrollTop;
        if (Math.abs(difference) < 0.75) {
          scrollable.scrollTop = state.target;
          state.frame = null;
          if (this.desktopWheelState === state) this.desktopWheelState = null;
          return;
        }
        scrollable.scrollTop += difference * (1 - Math.exp(-elapsed / 55));
        state.frame = requestAnimationFrame(step);
      };
      state.frame = requestAnimationFrame(step);
      return true;
    }

    preventBackgroundWheel(event) {
      if (!this.active && !this.desktopActive) return;
      const target = event.target;
      const pills = target?.closest?.('.ph-discovery__pills');

      if (this.desktopActive && this.tabletBodyLock) {
        if (event.ctrlKey || !event.cancelable) return;
        const x = event.clientX;
        const y = event.clientY;
        const predictive = this.desktopSearch?.querySelector('predictive-search[open] [data-predictive-search]');
        const hasPredictive = predictive?.isConnected && getComputedStyle(predictive).display !== 'none';
        const content = hasPredictive ? predictive : this.popular;
        const inPanel = this.isPointInside(this.panel, x, y) ||
          (hasPredictive && this.isPointInside(predictive, x, y)) ||
          this.panel?.contains(target) || this.desktopSearch?.contains(target);
        if (inPanel && !hasPredictive && this.isPointInside(this.pills, x, y)) {
          const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.pills.clientWidth : 1;
          const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
          this.scrollPillsWheel(this.pills, delta * factor);
        } else if (inPanel && content?.isConnected && getComputedStyle(content).display !== 'none') {
          const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? content.clientHeight : 1;
          this.scrollDesktopWheel(content, event.deltaY * factor);
        }
        event.preventDefault();
        return;
      }

      if (this.desktopActive) {
        if (event.ctrlKey || !event.cancelable) return;

        const bounds = this.panel?.getBoundingClientRect();
        const overPanel = bounds && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)
          && event.clientX >= bounds.left && event.clientX <= bounds.right
          && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
        if (!overPanel && !this.panel?.contains(target) && !this.desktopSearch?.contains(target)) return;

        if (pills && this.portal?.contains(pills) && pills.scrollWidth > pills.clientWidth + 1) {
          const horizontalTrackpad = Math.abs(event.deltaX) > Math.abs(event.deltaY);
          if (horizontalTrackpad && !event.shiftKey) return;
          const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pills.clientWidth : 1;
          const wheelDelta = event.shiftKey && horizontalTrackpad ? event.deltaX : event.deltaY;
          if (this.scrollPillsWheel(pills, wheelDelta * factor) || event.cancelable) event.preventDefault();
          return;
        }

        const predictive = this.desktopSearch?.querySelector('predictive-search[open] [data-predictive-search]');
        const hasPredictive = predictive?.isConnected && getComputedStyle(predictive).display !== 'none';
        const scrollable = hasPredictive ? predictive : this.popular;
        if (!scrollable?.isConnected || getComputedStyle(scrollable).display === 'none') return;
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

        const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scrollable.clientHeight : 1;
        if (this.scrollDesktopWheel(scrollable, event.deltaY * factor) || event.cancelable) event.preventDefault();
        return;
      }

      if (this.active) this.blurSearchInputs();
      if (!event.cancelable) return;
      if (pills && Math.abs(event.deltaX) > Math.abs(event.deltaY) && pills.scrollWidth > pills.clientWidth + 1) return;
      if (this.canScrollWithinOverlay(target, -event.deltaY)) return;
      event.preventDefault();
    }


    updateResultsTop() {
      if (!this.active || !this.searchForm) return;
      const bottom = Math.ceil(this.searchForm.getBoundingClientRect().bottom);
      (this.portal || this.section).style.setProperty('--ph-mobile-search-results-top', `${bottom}px`);
    }

    updateQueryState() {
      const hasQuery = this.input.value.trim().length > 0;
      this.section.classList.toggle('ph-mobile-search-has-query', hasQuery);
      this.portal?.classList.toggle('ph-mobile-search-has-query', hasQuery);
    }

    shuffle(items) {
      const result = [...items];
      for (let index = result.length - 1; index > 0; index--) {
        const other = Math.floor(Math.random() * (index + 1));
        [result[index], result[other]] = [result[other], result[index]];
      }
      return result;
    }

    shufflePopularPills() {
      if (!this.pills) return;
      const original = Array.from(this.pills.children);
      if (original.length < 2) return;
      const shuffled = this.shuffle(original);
      if (shuffled.every((pill, index) => pill === original[index])) {
        [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
      }
      this.pills.replaceChildren(...shuffled);
      this.pills.scrollLeft = 0;
    }

    sampleTerms(source, key) {
      const list = this.popular.querySelector(`[data-ph-discovery-links="${key}"]`);
      const group = this.popular.querySelector(`[data-ph-discovery-group="${key}"]`);
      if (!list || !group) return;
      list.replaceChildren();
      const unique = [...new Set(source.map((term) => String(term || '').trim()).filter(Boolean))];
      const terms = this.shuffle(unique).slice(0, 6);
      group.hidden = terms.length === 0;
      const base = this.searchForm?.getAttribute('action') || '/search';
      for (const term of terms) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const params = new URLSearchParams({ 'options[prefix]': 'last', type: 'product', q: term });
        link.href = `${base}?${params}`;
        link.textContent = term;
        link.className = 'ph-discovery__link';
        item.appendChild(link);
        list.appendChild(item);
      }
    }

    populateLists() {
      const categories = [...(this.section.querySelector('[data-ph-discovery-categories]')?.content.querySelectorAll('a') || [])]
        .map((link) => link.textContent);
      const parseArray = (selector) => {
        try {
          const data = JSON.parse(this.section.querySelector(selector)?.textContent || '[]');
          return Array.isArray(data) ? data : [];
        } catch (_) {
          return [];
        }
      };
      this.sampleTerms(categories, 'category');
      this.sampleTerms(parseArray('[data-ph-discovery-brands]'), 'brand');
      this.sampleTerms(parseArray('[data-ph-discovery-types]'), 'type');
    }

    populateDiscovery() {
      this.shufflePopularPills();
      this.populateLists();
      this.populateProducts();
    }

    async requestRandomPool(page, signal) {
      const cached = this.poolCache.get(page);
      if (cached && Date.now() - cached.time < 120000) return cached.items;
      if (cached) this.poolCache.delete(page);
      const allUrl = this.section.querySelector('[data-ph-mobile-search-root]')?.dataset.phSearchAllUrl || '/collections/all';
      const sectionName = this.section.querySelector('[data-ph-mobile-search-root]')?.dataset.phSearchRandomSection || 'ph-search-random-products';
      const url = new URL(allUrl, /^https?:\/\//i.test(allUrl) ? undefined : window.location.href);
      url.searchParams.set('section_id', sectionName);
      url.searchParams.set('page', String(page));
      const response = await fetch(url.toString(), { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Search suggestions unavailable');
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const pool = doc.querySelector('[data-ph-search-random-pool]');
      if (!pool) throw new Error('Search suggestions section not found');
      const items = this.shuffle([...pool.querySelectorAll(':scope > [data-ph-search-product-id]')]).slice(0, 20);
      if (!signal.aborted) {
        this.poolCache.delete(page);
        this.poolCache.set(page, { time: Date.now(), items });
        if (this.poolCache.size > 3) this.poolCache.delete(this.poolCache.keys().next().value);
      }
      return items;
    }

    async populateProducts() {
      if (!this.productList) return;
      const productLimit = 7;
      const current = ++this.productSequence;
      this.productAbort?.abort();
      const request = new AbortController();
      this.productAbort = request;
      const manualNodes = [...(this.section.querySelector('[data-ph-discovery-manual-products]')?.content.children || [])]
        .slice(0, productLimit).map((item) => item.cloneNode(true));
      const seen = new Set(manualNodes.map((item) => item.dataset.phSearchProductId));
      this.productList.replaceChildren(...manualNodes);
      if (manualNodes.length >= productLimit) return;
      const loading = document.createElement('li');
      loading.className = 'ph-discovery__loading';
      loading.textContent = 'Finding products for you...';
      this.productList.appendChild(loading);
      const pageCount = Math.max(1, parseInt(this.section.querySelector('[data-ph-mobile-search-root]')?.dataset.phSearchPageCount || '1', 10) || 1);
      const firstPageCount = Math.min(pageCount, 3, productLimit - seen.size);
      const pages = this.shuffle(Array.from({ length: firstPageCount }, (_, index) => {
        const first = Math.floor(index * pageCount / firstPageCount) + 1;
        const last = Math.floor((index + 1) * pageCount / firstPageCount);
        return first + Math.floor(Math.random() * (last - first + 1));
      }));
      const attemptedPages = new Set(pages);
      const chosen = [];
      const collectSamples = (groups) => {
        const buckets = this.shuffle(groups.filter((group) => group.length).map((group) => [...group]));
        while (seen.size < productLimit && buckets.some((bucket) => bucket.length)) {
          let added = false;
          for (const bucket of buckets) {
            while (bucket.length) {
              const sample = bucket.pop();
              const id = sample?.dataset?.phSearchProductId;
              if (!id || seen.has(id)) continue;
              seen.add(id);
              chosen.push(sample);
              added = true;
              break;
            }
            if (seen.size >= productLimit) break;
          }
          if (!added) break;
        }
      };
      try {
        const pools = await Promise.all(pages.map(async (page) => {
          try {
            return this.shuffle(await this.requestRandomPool(page, request.signal));
          } catch (error) {
            return [];
          }
        }));
        if (request.signal.aborted || this.productSequence !== current) return;
        collectSamples(pools);
        if (seen.size < productLimit && attemptedPages.size < Math.min(pageCount, 4)) {
          let extraPage;
          do {
            extraPage = 1 + Math.floor(Math.random() * pageCount);
          } while (attemptedPages.has(extraPage));
          attemptedPages.add(extraPage);
          try {
            collectSamples([this.shuffle(await this.requestRandomPool(extraPage, request.signal))]);
          } catch (error) {
            if (request.signal.aborted || error?.name === 'AbortError') return;
          }
        }
        if (request.signal.aborted || this.productSequence !== current) return;
        for (const sample of this.shuffle(chosen)) {
          const item = document.createElement('li');
          item.dataset.phSearchProductId = sample.dataset.phSearchProductId;
          item.innerHTML = sample.innerHTML;
          this.productList.insertBefore(item, loading);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') loading.textContent = '';
      } finally {
        if (this.productSequence === current) {
          loading.remove();
          if (!this.productList.children.length) {
            const item = document.createElement('li');
            item.className = 'ph-discovery__loading';
            const link = document.createElement('a');
            link.href = this.section.querySelector('[data-ph-mobile-search-root]')?.dataset.phSearchAllUrl || '/collections/all';
            link.textContent = 'Browse all products';
            item.appendChild(link);
            this.productList.appendChild(item);
          }
        }
      }
    }

    close(restoreFocus = true) {
      if (!this.active) return;
      if (this.closing) {
        if (!restoreFocus) this.finishClose(false);
        return;
      }
      this.closing = true;
      this.input.blur();
      if (!restoreFocus) {
        this.finishClose(false);
        return;
      }
      this.fadePanel(false, () => this.finishClose(true));
    }

    finishClose(restoreFocus) {
      if (!this.active) return;
      this.overlayAnimation?.cancel();
      this.overlayAnimation = null;
      this.active = false;
      this.closing = false;
      this.predictive?.close?.(true);
      this.unmountOverlay();
      if (this.searchForm && this.launchButton) this.searchForm.inert = true;
      if (this.launchButton) this.launchButton.setAttribute('aria-expanded', 'false');
      this.section.classList.remove('ph-mobile-search-slot-occupied', 'ph-mobile-search-has-query');
      this.section.style.removeProperty('--ph-mobile-search-results-top');
      this.section.style.removeProperty('--ph-mobile-search-slot-height');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.panel.removeAttribute('aria-label');
      this.trigger.setAttribute('aria-expanded', 'false');
      const restoreTo = this.savedPageScrollY;
      this.savedPageScrollY = null;
      if (restoreTo !== null) this.restorePagePosition(restoreTo);
      this.updateCompact(true);
      if (restoreFocus && this.returnFocusTo?.isConnected) this.returnFocusTo.focus({ preventScroll: true });
      this.returnFocusTo = null;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.active && !this.closing) {
          event.preventDefault();
          this.close();
        } else if (this.desktopActive) {
          event.preventDefault();
          this.closeDesktop();
        }
        return;
      }
      if (!this.active || this.closing) return;
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) {
        if (!event.target?.closest?.('input, select, textarea, button, a, .ph-mobile-search__popular, [data-predictive-search]')) {
          event.preventDefault();
        }
      }
      if (event.key !== 'Tab') return;
      const focusables = [...this.panel.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled])')]
        .filter((element) => element.getClientRects().length > 0);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    destroy() {
      this.productAbort?.abort();
      this.poolCache.clear();
      this.overlayAnimation?.cancel();
      this.overlayAnimation = null;
      if (this.active) this.finishClose(false);
      if (this.desktopActive) this.finishDesktopClose();
      if (this.viewportFrame !== null) cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = null;
      this.stopPillWheelAnimation();
      this.cancelTouchTabletMomentum();
      this.unlockTouchTabletPage();
      if (this.pillDragFrame !== null) cancelAnimationFrame(this.pillDragFrame);
      this.pillDragFrame = null;
      this.pillDrag = null;
      this.abort.abort();
      this.header?.classList.remove('ph-mobile-search-compact');
      this.section.classList.remove('ph-search-launch-ready');
      if (this.searchForm) this.searchForm.inert = false;
      delete this.section.phMobileSearchController;
    }
  }

  const initialize = (scope = document) => {
    const sections = scope.matches?.('.ph-mobile-search-section')
      ? [scope]
      : [...scope.querySelectorAll('.ph-mobile-search-section:not(.ph-mobile-search-overlay-portal)')];
    sections.forEach((section) => {
      if (section.phMobileSearchController) return;
      section.phMobileSearchController = new MobileHeaderSearchController(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
